import { MealType } from '@akin-home/amplify';
import { createChecklistItem } from '@akin-home/checklists';
import {
  createMealFromRecipe,
  importRecipeToMeal,
  replaceMealRecipe,
} from '@akin-home/meals';
import { createIngredient, createRecipe } from '@akin-home/recipes';
import { findByID, removeByID, setByID } from '@akin-home/records';
import { CheckListItem } from '../models';
import { palette } from '../theme';
import { generateId } from '../utils/records';
import { append, generateListFieldFunctionsReordered } from './../utils/arrays';
import {
  Ingredient,
  MealAvoidance,
  MealAvoidanceCategory,
  MealAvoidanceDegree,
  PrepNote,
  PrepNoteIcon,
  Recipe,
} from './types';

export interface RecommendedRecipes {
  Breakfast: any[];
  Lunch: any[];
  Dinner: any[];
  Snack: any[];
}

export const createPrepNote = (input?: Partial<PrepNote>): PrepNote => ({
  id: generateId(),
  description: '',
  title: '',
  ...input,
});

export const getPrepNoteIconFileName = (note: PrepNote): string => {
  const icon = prepNoteIconMap[note.icon || PrepNoteIcon.bake];

  return `Icon_${icon}.svg`;
};

export const prepNoteIconMap: Record<PrepNoteIcon, string> = {
  [PrepNoteIcon.bake]: 'bake',
  [PrepNoteIcon.bowl]: 'bowl',
  [PrepNoteIcon.chopping_table]: 'chopping_table',
  [PrepNoteIcon.cook_chicken]: 'cook_chicken',
  [PrepNoteIcon.fry_pan]: 'fry_pan',
  [PrepNoteIcon.serve]: 'serve',
};

export const recipeHealthRatingLabels: Record<1 | 2 | 3, string> = {
  1: 'Indulgent',
  2: 'Average',
  3: 'Healthy',
};

export const recipeBudgetRatingLabels: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: 'Cheapest',
  2: 'Cheap',
  3: 'Mid-range',
  4: 'Semi-gourmet',
  5: 'Gourmet',
};

export const recipeBudgetRatings = Object.entries(recipeBudgetRatingLabels).map(
  (entry) => ({
    id: parseInt(entry[0]),
    title: entry[1],
  })
);

export const recipeHealthRatings = Object.entries(recipeHealthRatingLabels).map(
  (entry) => ({
    id: parseInt(entry[0]),
    title: entry[1],
  })
);

const [
  addRecipeUtensil,
  updateRecipeUtensil,
  deleteRecipeUtensil,
  reorderRecipeUtensilUp,
  reorderRecipeUtensilDown,
] = generateListFieldFunctionsReordered<Recipe, ''>('utensils', '');

const [
  addRecipeIngredient,
  updateRecipeIngredient,
  deleteRecipeIngredient,
  moveIngredientBack,
  moveIngredientForward,
] = generateListFieldFunctionsReordered<Recipe, Ingredient>(
  'ingredients',
  createIngredient()
);

const [
  addRecipePrepNote,
  updateRecipePrepNote,
  deleteRecipePrepNote,
  reorderRecipePrepNoteUp,
  reorderRecipePrepNoteDown,
] = generateListFieldFunctionsReordered<Recipe, PrepNote>(
  'prepNotes',
  createPrepNote()
);

export {
  addRecipeIngredient,
  addRecipePrepNote,
  addRecipeUtensil,
  createIngredient,
  createMealFromRecipe,
  createRecipe,
  deleteRecipeIngredient,
  deleteRecipePrepNote,
  deleteRecipeUtensil,
  importRecipeToMeal,
  moveIngredientBack,
  moveIngredientForward,
  reorderRecipePrepNoteDown,
  reorderRecipePrepNoteUp,
  reorderRecipeUtensilDown,
  reorderRecipeUtensilUp,
  replaceMealRecipe,
  updateRecipeIngredient,
  updateRecipePrepNote,
  updateRecipeUtensil,
};

export const getColorForMealType = (mealType: MealType): string =>
  palette[mealType] || palette.black;

export const toggleMealAvoidance = (
  avoidances: MealAvoidance[],
  avoidance_category: MealAvoidanceCategory
): MealAvoidance[] => {
  const categories = avoidances.map((a) => a.category);

  return categories.includes(avoidance_category)
    ? avoidances.filter((a) => a.category !== avoidance_category)
    : [
        ...avoidances,
        {
          category: avoidance_category,
          degree: MealAvoidanceDegree.ALL_OF_THE_TIME,
        },
      ];
};

export const setMealAvoidanceDegree = (
  avoidances: MealAvoidance[],
  avoidance: MealAvoidance
): MealAvoidance[] => {
  return avoidances.map((a) =>
    a.category === avoidance.category ? avoidance : a
  );
};

export type AddRecipeIngredientParams = {
  recipe_id: string;
  quantity?: number;
  name: string;
  unit?: string;
};

export const addRecipeIngredientToShoppingListItems =
  (items: CheckListItem[]) =>
  (params: AddRecipeIngredientParams): CheckListItem[] => {
    const { recipe_id, quantity, name, unit } = params;

    const matching_item = items.find((i) => i.title === name);

    if (!matching_item) {
      return [
        ...items,
        createChecklistItem({
          title: name,
          unit: unit,
          recipes: [{ recipe_id, quantity }],
        }),
      ];
    } else {
      return setByID(items, {
        ...matching_item,
        unit: unit,
        recipes: append(matching_item.recipes, { recipe_id, quantity }),
      });
    }
  };

export const removeRecipeIngredientFromShoppingListItems = (
  items: CheckListItem[],
  item_id: string,
  recipe_id: string
): CheckListItem[] => {
  const item = findByID(items, item_id);

  if (!item) return items;

  const { recipes } = item;

  const recipe = recipes?.find((recipe) => recipe.recipe_id === recipe_id);

  /** No recipes linked, just remove the item from the list */
  if (!item.recipes || !recipe) {
    return removeByID(items, item_id);
  }

  const modified_item = {
    ...item,
    recipes: item.recipes.filter((r) => r.recipe_id !== recipe_id),
  };

  /** The item has no more recipes linked, just remove the item */
  if (!modified_item.recipes?.length) {
    return removeByID(items, item_id);
  }

  /** Item still has other recipes linked, keep it */
  return setByID(items, modified_item);
};
